import React from 'react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Icon } from '@/shared/icons';
import { Badge } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Tabs } from '@/shared/components/ui/Tabs';
import { CREDIT_CATEGORIES } from '@troia-v3/types';
import type { CreditCategory } from '@troia-v3/types';
import { useInvoiceDetail } from '../hooks';
import type { CreditTransactionResponse, InvoiceCategoryConsumption } from '../types';
import { formatDateSafe, type DateInput } from '../utils/formatDateSafe';

interface InvoiceDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cycleNumber: number | null;
}

const formatDate = (d: DateInput) =>
  formatDateSafe(d, { day: '2-digit', month: 'short', year: 'numeric' });

const formatDateTime = (d: DateInput) =>
  formatDateSafe(d, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const formatNumber = (n: number) =>
  new Intl.NumberFormat('pt-BR').format(n);

const formatCurrency = (amount: number, currency = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount);

const getCategoryLabel = (category: string): string =>
  CREDIT_CATEGORIES[category as CreditCategory]?.label || category;

// Tabs are built inside the component to pass dynamic content

/**
 * Drawer showing invoice details with summary, consumption by category, and transactions.
 * Uses Drawer shared component (light theme enforced automatically).
 */
export const InvoiceDetailDrawer: React.FC<InvoiceDetailDrawerProps> = ({
  isOpen,
  onClose,
  cycleNumber,
}) => {
  const { invoiceDetail, isLoading } = useInvoiceDetail(isOpen ? (cycleNumber ?? undefined) : undefined);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={cycleNumber ? `Fatura #${cycleNumber}` : 'Fatura'}
      icon={<Icon name="file-text" className="h-5 w-5" />}
      size="lg"
    >
      <div className="px-6 py-5">
      {isLoading ? (
        <div className="space-y-6">
          <Skeleton variant="rectangular" height="120px" />
          <Skeleton variant="text" lines={5} />
        </div>
      ) : !invoiceDetail ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-sm" style={{ color: '#64748b' }}>
            Nenhum detalhe encontrado
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Period & Status */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium" style={{ color: '#64748b' }}>Período</p>
              <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                {formatDate(invoiceDetail.invoice.period.start)} - {formatDate(invoiceDetail.invoice.period.end)}
              </p>
            </div>
            <Badge
              variant={invoiceDetail.invoice.status === 'closed' ? 'success' : invoiceDetail.invoice.status === 'cancelled' ? 'destructive' : 'warning'}
              dot
            >
              {invoiceDetail.invoice.status === 'closed' ? 'Fechada' : invoiceDetail.invoice.status === 'cancelled' ? 'Cancelada' : 'Aberta'}
            </Badge>
          </div>

          {/* Payment info */}
          {invoiceDetail.invoice.payment && (
            <div
              className="rounded-lg p-3"
              style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}
            >
              <span className="text-xs font-medium mb-2 block" style={{ color: '#64748b' }}>
                Pagamento
              </span>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  {invoiceDetail.invoice.planName && (
                    <span className="text-sm block" style={{ color: '#1e293b' }}>
                      {invoiceDetail.invoice.planName}
                    </span>
                  )}
                  {invoiceDetail.invoice.payment.discount > 0 && (
                    <div className="flex gap-3 text-xs" style={{ color: '#64748b' }}>
                      <span>Subtotal: {formatCurrency(invoiceDetail.invoice.payment.amount, invoiceDetail.invoice.payment.currency)}</span>
                      <span style={{ color: '#10b981' }}>Desconto: -{formatCurrency(invoiceDetail.invoice.payment.discount, invoiceDetail.invoice.payment.currency)}</span>
                    </div>
                  )}
                </div>
                <span className="text-lg font-bold" style={{ color: '#1e293b' }}>
                  {formatCurrency(invoiceDetail.invoice.payment.total, invoiceDetail.invoice.payment.currency)}
                </span>
              </div>
            </div>
          )}

          {/* Summary breakdown */}
          <div
            className="rounded-xl border p-4 space-y-3"
            style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb' }}
          >
            <h4 className="text-sm font-semibold" style={{ color: '#1e293b' }}>
              Resumo
            </h4>
            <div className="space-y-2">
              <SummaryRow label="Saldo anterior" value={invoiceDetail.invoice.summary.balanceBefore} />
              <SummaryRow label="Créditos adicionados" value={invoiceDetail.invoice.summary.totalCreditsIn} positive />
              <SummaryRow label="Créditos consumidos" value={invoiceDetail.invoice.summary.totalCreditsOut} negative />
              <SummaryRow label="Expirados" value={invoiceDetail.invoice.summary.expired} negative />
              <div className="border-t pt-2" style={{ borderColor: '#e5e7eb' }}>
                <SummaryRow
                  label="Saldo final"
                  value={invoiceDetail.invoice.summary.balanceAfter}
                  bold
                />
              </div>
            </div>
          </div>

          {/* Tabs: By category / Transactions */}
          <Tabs
            tabs={[
              {
                id: 'categories',
                label: 'Por categoria',
                content: <CategoryConsumptionList items={invoiceDetail.consumptionByCategory} />,
              },
              {
                id: 'transactions',
                label: 'Extrato',
                content: <TransactionsList transactions={invoiceDetail.transactions} />,
              },
            ]}
            defaultTab="categories"
            variant="underline"
            fullWidth
          />
        </div>
      )}
      </div>
    </Drawer>
  );
};

/** Summary row helper */
const SummaryRow: React.FC<{
  label: string;
  value: number;
  positive?: boolean;
  negative?: boolean;
  bold?: boolean;
}> = ({ label, value, positive, negative, bold }) => (
  <div className="flex items-center justify-between">
    <span
      className={`text-sm ${bold ? 'font-semibold' : ''}`}
      style={{ color: bold ? '#1e293b' : '#64748b' }}
    >
      {label}
    </span>
    <span
      className={`text-sm ${bold ? 'font-semibold' : 'font-medium'}`}
      style={{
        color: positive ? '#10b981' : negative && value > 0 ? '#ef4444' : '#1e293b',
      }}
    >
      {positive ? '+' : negative && value > 0 ? '-' : ''}{formatNumber(value)}
    </span>
  </div>
);

/** Category consumption list */
const CategoryConsumptionList: React.FC<{
  items: InvoiceCategoryConsumption[];
}> = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <p className="text-sm text-center py-4" style={{ color: '#64748b' }}>
        Nenhum consumo neste ciclo
      </p>
    );
  }

  const maxTotal = Math.max(...items.map((i) => i.total));

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const pct = maxTotal > 0 ? (item.total / maxTotal) * 100 : 0;
        return (
          <div key={item.category} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: '#1e293b' }}>
                {getCategoryLabel(item.category)}
              </span>
              <span className="text-sm font-medium" style={{ color: '#ef4444' }}>
                -{formatNumber(item.total)}
              </span>
            </div>
            <div
              className="h-1.5 rounded-full overflow-hidden"
              style={{ backgroundColor: '#e5e7eb' }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  backgroundColor: '#8b5cf6',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Transactions list (extrato) */
const TransactionsList: React.FC<{
  transactions: CreditTransactionResponse[];
}> = ({ transactions }) => {
  if (transactions.length === 0) {
    return (
      <p className="text-sm text-center py-4" style={{ color: '#64748b' }}>
        Nenhuma transacao neste ciclo
      </p>
    );
  }

  return (
    <div className="space-y-0">
      {transactions.map((tx: CreditTransactionResponse, index: number) => (
        <div
          key={tx.id}
          className={`flex items-center justify-between py-3 ${index > 0 ? 'border-t' : ''}`}
          style={index > 0 ? { borderColor: '#e5e7eb' } : {}}
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
              {getCategoryLabel(tx.category)}
            </p>
            <p className="text-xs" style={{ color: '#64748b' }}>
              {formatDateTime(tx.createdAt)}
            </p>
          </div>
          <div className="text-right ml-4">
            <p
              className="text-sm font-medium"
              style={{ color: tx.type === 'credit' ? '#10b981' : '#ef4444' }}
            >
              {tx.type === 'credit' ? '+' : '-'}{formatNumber(tx.amount)}
            </p>
            <p className="text-xs" style={{ color: '#64748b' }}>
              Saldo: {formatNumber(tx.balanceAfter)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

InvoiceDetailDrawer.displayName = 'InvoiceDetailDrawer';
